import React from 'react'
import type { PublicSuggestion } from 'apollo-gql'
import { Trans, useTranslation } from 'lib/i18n'
import { pushToDataLayer } from 'lib/tracking'

import Link from 'components/Link/Link'
import { Box } from 'components/UI/Box/Box'
import { Button } from 'components/UI/Button/Button'
import { Card } from 'components/UI/Card/Card'
import {
  BodySmall,
  DesktopH2,
  H3,
} from 'components/UI/Typography/Typography.new'
import Wrapper from 'components/Wrapper/Wrapper'

import styles from './BusinessSuggestions.module.css'

const BusinessSuggestions = ({
  suggestions,
}: {
  suggestions: PublicSuggestion[]
}): JSX.Element => {
  const { t } = useTranslation()

  const logCta = () =>
    pushToDataLayer({
      event: 'showAllTalentsClick',
    })

  return (
    <Wrapper edgeToEdge className={styles.businessSuggestions}>
      <div className={styles.textWrapper}>
        <div className={styles.title}>
          <DesktopH2 element="h2">
            <Trans i18nKey="BusinessSuggestions.title">
              Order <span className={styles.blue}>today!</span>
            </Trans>
          </DesktopH2>
        </div>
        <div className={styles.description}>
          <H3 element="h3">
            <Trans i18nKey="BusinessSuggestions.description">
              Here are three suggestions to get you started, but there are lots
              more at <span className={styles.blue}>memmo.me</span>
            </Trans>
          </H3>
        </div>
        <div className={styles.advice}>
          <BodySmall theme="tertiary">
            {t(
              'BusinessSuggestions.advice',
              'You can always reach out to us if you need help finding the perfect talent.',
            )}
          </BodySmall>
        </div>
      </div>
      <div className={styles.suggestions} data-cy="businessSuggestions">
        {suggestions.map(({ profile, testimonial, customVideo }) => (
          <div
            className={styles.suggestionCard}
            key={profile.id}
            data-cy="businessSuggestionsCard"
          >
            <Card
              talentName={profile.name}
              talentDescription={profile.title}
              imageUrl={profile.profilePicture?.cardUrl}
              videoUrl={customVideo?.url || profile.profileVideo?.url}
              size="big"
              type="featured"
            />
            {testimonial && (
              <Box className={styles.testimonial}>
                <BodySmall element="p" theme="tertiary">
                  {testimonial}
                </BodySmall>
              </Box>
            )}
          </div>
        ))}
      </div>
      <div className={styles.buttonWrapper}>
        <Link href="/page/business-find-talent">
          <a>
            <Button onClick={logCta} type="secondary">
              {t('BusinessSuggestions.showAll', 'Show all talents')}
            </Button>
          </a>
        </Link>
      </div>
    </Wrapper>
  )
}

export default BusinessSuggestions
